// Singleton speech-synthesis hook. Only one section speaks at a time.
import { useCallback, useEffect, useRef, useState } from "react";

let currentId = null;
const listeners = new Set();

const notify = () => listeners.forEach((fn) => fn(currentId));

export default function useReadAloud(id, text) {
    const [active, setActive] = useState(() => currentId === id);
    const utterRef = useRef(null);

    useEffect(() => {
        const fn = (cur) => setActive(cur === id);
        listeners.add(fn);
        return () => {
            listeners.delete(fn);
        };
    }, [id]);

    const stop = useCallback(() => {
        if (typeof window === "undefined") return;
        window.speechSynthesis?.cancel();
        currentId = null;
        notify();
    }, []);

    const start = useCallback(() => {
        if (typeof window === "undefined") return;
        if (!("speechSynthesis" in window)) return;
        window.speechSynthesis.cancel();

        // Chunk text into ~180-char pieces so Chrome doesn't clip long utterances.
        const HARD_CAP = 200;
        const splitLong = (piece) => {
            if (piece.length <= HARD_CAP) return [piece];
            const words = piece.split(/\s+/);
            const out = [];
            let buf = "";
            for (const w of words) {
                if ((buf + " " + w).trim().length > HARD_CAP && buf) {
                    out.push(buf.trim());
                    buf = w;
                } else {
                    buf = buf ? buf + " " + w : w;
                }
            }
            if (buf.trim()) out.push(buf.trim());
            return out;
        };
        const parts = text.match(/[^.!?;:]+[.!?;:]+|\S+/g) || [text];
        const chunks = [];
        let buf = "";
        for (const p of parts) {
            if ((buf + " " + p).trim().length > 180 && buf) {
                chunks.push(...splitLong(buf.trim()));
                buf = p;
            } else {
                buf = (buf ? buf + " " : "") + p;
            }
        }
        if (buf.trim()) chunks.push(...splitLong(buf.trim()));
        if (!chunks.length) return;

        currentId = id;
        notify();

        let idx = 0;
        const speakNext = () => {
            if (currentId !== id) return;
            if (idx >= chunks.length) {
                currentId = null;
                notify();
                return;
            }
            const chunk = chunks[idx++];
            const u = new SpeechSynthesisUtterance(chunk);
            u.rate = 0.95;
            u.pitch = 1;
            u.lang = "en-CA";
            u.onend = () => {
                if (currentId === id) speakNext();
            };
            u.onerror = () => {
                if (currentId === id) speakNext();
            };
            utterRef.current = u;
            try {
                window.speechSynthesis.speak(u);
            } catch (e) {
                speakNext();
            }
        };
        speakNext();
    }, [id, text]);

    const toggle = useCallback(() => {
        if (currentId === id) stop();
        else start();
    }, [id, start, stop]);

    // Stop any in-flight utterance when unmounted / route change
    useEffect(() => {
        return () => {
            if (currentId === id) {
                window.speechSynthesis?.cancel();
                currentId = null;
                notify();
            }
        };
    }, [id]);

    const supported =
        typeof window !== "undefined" && "speechSynthesis" in window;
    return { active, toggle, supported };
}
